import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAktiveModule } from '../hooks/useAktiveModule';
import { useRechte } from '../hooks/useRechte';
import { ALL_TABS, TabType, benutzerRollen, sichtbareTabIds, standardTabId } from '../lib/tabRollen';
import PartnerList from '../components/PartnerList';
import ArtikelList from '../components/ArtikelList';
import LagerbestandList from '../components/LagerbestandList';
import LagerbewegungList from '../components/LagerbewegungList';
import BestellungList from '../components/BestellungList';
import FertigungsauftragList from '../components/FertigungsauftragList';
import QualitaetOverview from '../components/QualitaetOverview';
import AuftragList from '../components/AuftragList';
import DokumenteOverview from '../components/DokumenteOverview';
import WarnungenList from '../components/WarnungenList';
import MusterVorschlaege from '../components/MusterVorschlaege';
import KanbanBoard from '../components/KanbanBoard';
import ImportWizard from '../components/ImportWizard';
import StuecklistenImport from '../components/StuecklistenImport';
import KatalogeVerwaltung from '../components/KatalogeVerwaltung';
import AufgabenOverview from '../components/AufgabenOverview';
import EinsatzPlantafel from '../components/EinsatzPlantafel';
import ProjektList from '../components/ProjektList';
import ZeitbuchungList from '../components/ZeitbuchungList';
import MaterialentnahmeList from '../components/MaterialentnahmeList';
import VertragList from '../components/VertragList';
import ZahlungList from '../components/ZahlungList';
import RetoureList from '../components/RetoureList';
import SendungList from '../components/SendungList';
import ProzessKatalog from '../components/ProzessKatalog';
import RegieberichteOverview from '../components/RegieberichteOverview';
import {
  NAVIGIERE_EVENT, NavigiereDetail, setAuftragNavigationVerfuegbar, setPartnerNavigationVerfuegbar,
  setVertragNavigationVerfuegbar,
} from '../lib/navigation';
import './ManagementPage.css';

interface AuthUser {
  name: string;
  email: string;
  rolle: string;
  tenant_id: number | null;
  tenant_slug: string | null;
  firmenname: string | null;
  primaerfarbe: string | null;
}

interface ManagementPageProps {
  userRolle?: string;
  authToken?: string;
  authUser?: AuthUser | null;
  initialTab?: string;
  /** Navigationsparameter zum initialTab (z. B. `auftrag_id`), aus NAVIGIERE_EVENT vor dem Mount. */
  initialParams?: Record<string, unknown>;
  onInitialTabConsumed?: () => void;
}

const ManagementPage: React.FC<ManagementPageProps> = ({ userRolle, authToken, authUser, initialTab, initialParams, onInitialTabConsumed }) => {
  const aktiveModule = useAktiveModule();
  // Alle Rollen des Benutzers (Zusatzrollen zählen mit); bis /api/me/rechte geladen
  // ist, gilt die Hauptrolle. Tabs werden bewusst NICHT über <modul>.lesen gesteuert.
  const { rollen: geladeneRollen, geladen: rollenGeladen } = useRechte();
  const rollen = benutzerRollen(geladeneRollen, rollenGeladen, userRolle);

  // Tabs filtern basierend auf Rollen (gleiche Regel wie die Erreichbarkeit der
  // Dashboard-Kacheln, lib/tabRollen.sichtbareTabIds) UND aktiven Modulen
  const rollenTabs = new Set<string>(sichtbareTabIds(rollen));
  const sichtbareTabs = ALL_TABS.filter(tab => {
    const rolleOk = rollenTabs.has(tab.id);
    // Wenn Modul-Set noch leer (loading), zeige alle Tabs (kein Flicker)
    const modulOk = !tab.modul || aktiveModule.size === 0 || aktiveModule.has(tab.modul);
    return rolleOk && modulOk;
  });

  const [activeTab, setActiveTab] = useState<TabType>(
    standardTabId(sichtbareTabs.map(t => t.id)) ?? 'warnungen'
  );

  const [pendingAuftragId, setPendingAuftragId] = useState<number | null>(null);
  const [pendingPartnerId, setPendingPartnerId] = useState<number | null>(null);
  const [pendingVertragId, setPendingVertragId] = useState<number | null>(null);
  // `datum` (yyyy-mm-dd) zum Tab `einsaetze`: die Plantafel springt in die Woche dieses
  // Tages (Termine der Kundenakte). Nach dem Anwenden zurücksetzen, sonst zöge jeder
  // spätere Wechsel in die Einsatzplanung wieder in dieselbe Woche.
  const [pendingEinsatzDatum, setPendingEinsatzDatum] = useState<string | null>(null);
  const einsatzDatumVerbraucht = useCallback(() => setPendingEinsatzDatum(null), []);

  // Rollen wie Projektleiter/Projektmitarbeiter/Buchhaltung sehen den
  // Regieberichte-Tab, aber NICHT den Aufträge-Tab — für sie darf
  // navigiereZuAuftrag() weder den Tab wechseln (currentTab fiele sonst auf
  // den Standard-Tab zurück) noch die Chips
  // anzeigen, die dorthin verlinken. Per Ref statt nur als Closure-Wert im
  // Effekt, damit der einmalig registrierte Listener unten trotzdem den
  // jeweils aktuellen Stand sieht, statt den vom Mount einzufrieren.
  const auftragTabSichtbar = sichtbareTabs.some(t => t.id === 'auftraege');
  const auftragTabSichtbarRef = useRef(auftragTabSichtbar);
  auftragTabSichtbarRef.current = auftragTabSichtbar;

  useEffect(() => {
    setAuftragNavigationVerfuegbar(auftragTabSichtbar);
  }, [auftragTabSichtbar]);

  // Dasselbe für die Kundenakte (navigiereZuPartner) und den Kontakte-Tab.
  const kundenTabSichtbar = sichtbareTabs.some(t => t.id === 'kunden');
  const kundenTabSichtbarRef = useRef(kundenTabSichtbar);
  kundenTabSichtbarRef.current = kundenTabSichtbar;

  useEffect(() => {
    setPartnerNavigationVerfuegbar(kundenTabSichtbar);
  }, [kundenTabSichtbar]);

  // Dasselbe für die Vertragsakte (navigiereZuVertrag) und den Verträge-Tab.
  const vertragTabSichtbar = sichtbareTabs.some(t => t.id === 'vertraege');
  const vertragTabSichtbarRef = useRef(vertragTabSichtbar);
  vertragTabSichtbarRef.current = vertragTabSichtbar;

  useEffect(() => {
    setVertragNavigationVerfuegbar(vertragTabSichtbar);
  }, [vertragTabSichtbar]);

  const sichtbareTabIdsRef = useRef<string[]>([]);
  sichtbareTabIdsRef.current = sichtbareTabs.map(t => t.id);

  // Navigationsziel anwenden (Tab + Parameter). Liefert true, wenn etwas
  // angewendet wurde. `auftrag_id` öffnet die Auftragsakte — nur, wenn die
  // Rolle den Aufträge-Tab sieht; `partner_id` analog die Kundenakte im
  // Kontakte-Tab, `vertrag_id` die Vertragsakte im Verträge-Tab; unbekannte
  // Parameter werden ignoriert.
  const wendeNavigationAn = useRef((tab: string | null, params: Record<string, unknown>): boolean => {
    const auftragId = params.auftrag_id;
    if (typeof auftragId === 'number') {
      if (!auftragTabSichtbarRef.current) return false;
      setPendingAuftragId(auftragId);
      setActiveTab('auftraege');
      return true;
    }
    const partnerId = params.partner_id;
    if (typeof partnerId === 'number') {
      if (!kundenTabSichtbarRef.current) return false;
      setPendingPartnerId(partnerId);
      setActiveTab('kunden');
      return true;
    }
    const vertragId = params.vertrag_id;
    if (typeof vertragId === 'number') {
      if (!vertragTabSichtbarRef.current) return false;
      setPendingVertragId(vertragId);
      setActiveTab('vertraege');
      return true;
    }
    if (tab && sichtbareTabIdsRef.current.includes(tab)) {
      if (tab === 'einsaetze' && typeof params.datum === 'string') setPendingEinsatzDatum(params.datum);
      setActiveTab(tab as TabType);
      return true;
    }
    return false;
  }).current;

  // Navigation erst anwenden, wenn die Rollen geladen sind: bis dahin gilt nur die
  // Hauptrolle, und ein Sprung in einen Tab einer Zusatzrolle (z. B. Verkäufer +
  // Buchhaltung -> Dokumente) würde fälschlich abgewiesen. Bis dahin bleibt das Ziel liegen.
  const rollenGeladenRef = useRef(rollenGeladen);
  rollenGeladenRef.current = rollenGeladen;
  const [wartendesEventZiel, setWartendesEventZiel] = useState<NavigiereDetail | null>(null);

  // Bereits gemountet: Navigation (Dashboards-Kachel, Belegkarte, Regiebericht-
  // Chip …) selbst anwenden. Vor dem Mount reicht App Tab/Parameter über
  // initialTab/initialParams durch.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<NavigiereDetail>).detail;
      if (!detail || detail.ansicht !== 'management') return;
      if (rollenGeladenRef.current) {
        wendeNavigationAn(detail.tab, detail.params ?? {});
      } else {
        setWartendesEventZiel(detail);
      }
    };
    window.addEventListener(NAVIGIERE_EVENT, handler);
    return () => window.removeEventListener(NAVIGIERE_EVENT, handler);
  }, [wendeNavigationAn]);

  useEffect(() => {
    if (!rollenGeladen || !wartendesEventZiel) return;
    wendeNavigationAn(wartendesEventZiel.tab, wartendesEventZiel.params ?? {});
    setWartendesEventZiel(null);
  }, [rollenGeladen, wartendesEventZiel, wendeNavigationAn]);

  // Wenn initialTab/initialParams gesetzt (z.B. von Command Palette oder einer
  // Dashboard-Kachel vor dem Wechsel in die Verwaltung), dorthin wechseln - sobald die
  // Rollen geladen sind. Danach immer als verbraucht melden — auch wenn der Sprung für
  // die Rolle nicht erlaubt war —, sonst bliebe z. B. ein abgewiesenes auftrag_id liegen
  // und lenkte jeden späteren initialTab in den Auftrags-Zweig.
  useEffect(() => {
    if (!rollenGeladen) return;
    if (!initialTab && !initialParams) return;
    wendeNavigationAn(initialTab ?? null, initialParams ?? {});
    onInitialTabConsumed?.();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab, initialParams, rollenGeladen]);

  // Wenn aktiver Tab nicht mehr sichtbar → Standard-Tab wählen
  const currentTab = sichtbareTabs.find(t => t.id === activeTab)
    ? activeTab
    : (standardTabId(sichtbareTabs.map(t => t.id)) ?? 'warnungen');

  return (
    <div className="management-page">
      <div className="tab-navigation">
        {sichtbareTabs.map(tab => (
          <button
            key={tab.id}
            className={`tab-button ${currentTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="tab-content">
        {currentTab === 'warnungen'    && <WarnungenList authToken={authToken || ''} />}
        {currentTab === 'kunden'       && (
          <PartnerList
            initialPartnerId={pendingPartnerId}
            onInitialPartnerConsumed={() => setPendingPartnerId(null)}
          />
        )}
        {currentTab === 'artikel'      && <ArtikelList />}
        {currentTab === 'lager'        && <LagerbestandList />}
        {currentTab === 'bewegungen'   && <LagerbewegungList />}
        {currentTab === 'bestellungen' && <BestellungList />}
        {currentTab === 'fertigung'    && <FertigungsauftragList />}
        {currentTab === 'qualitaet'    && <QualitaetOverview />}
        {currentTab === 'auftraege'    && (
          <AuftragList
            initialAuftragId={pendingAuftragId}
            onInitialAuftragConsumed={() => setPendingAuftragId(null)}
          />
        )}
        {currentTab === 'aufgaben'     && <AufgabenOverview authToken={authToken || ''} />}
        {currentTab === 'dokumente'    && <DokumenteOverview />}
        {currentTab === 'vertraege'    && (
          <VertragList
            initialAkteId={pendingVertragId}
            onInitialAkteConsumed={() => setPendingVertragId(null)}
          />
        )}
        {currentTab === 'zahlungen'    && <ZahlungList />}
        {currentTab === 'retouren'     && <RetoureList />}
        {currentTab === 'sendungen'    && <SendungList />}
        {currentTab === 'muster'          && <MusterVorschlaege authToken={authToken || ''} />}
        {currentTab === 'prozesse'        && <ProzessKatalog authToken={authToken || ''} />}
        {currentTab === 'kanban'          && <KanbanBoard authToken={authToken || ''} />}
        {currentTab === 'import'          && <ImportWizard authToken={authToken || ''} />}
        {currentTab === 'stuecklisten'    && <StuecklistenImport authToken={authToken || ''} />}
        {currentTab === 'kataloge'        && <KatalogeVerwaltung authToken={authToken || ''} />}

        {currentTab === 'projekte'       && <ProjektList authToken={authToken || ''} userRolle={userRolle} />}
        {currentTab === 'zeitbuchung'    && <ZeitbuchungList />}
        {currentTab === 'materialentnahme' && <MaterialentnahmeList />}
        {currentTab === 'einsaetze' && (
          <EinsatzPlantafel
            userRolle={userRolle}
            initialDatum={pendingEinsatzDatum}
            onInitialDatumConsumed={einsatzDatumVerbraucht}
          />
        )}
        {currentTab === 'regieberichte' && <RegieberichteOverview authToken={authToken || ''} userRolle={userRolle} />}
      </div>
    </div>
  );
};

export default ManagementPage;
